import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { ChevronLeft } from "lucide-react";
import { cn } from "@/utils/helpers";
import {
  MEDIA_TYPE_LABEL,
  displayAspectRatio,
  type CalendarPlanSlot,
} from "@/types/contentCalendar.type";
import type { PostState, TurnOwner } from "../postState";
import StageTrack from "./StageTrack";

/** The turn line's ink per owner. Late, Missed and Didn't post take the alert ink; nothing else is coloured. */
function turnInk(owner: TurnOwner, alert = false): string {
  if (alert || owner === "late") return "var(--co-alert-ink)";
  if (owner === "you") return "var(--co-accent-ink)";
  return "var(--co-muted)";
}

/** The turn dot: filled for your turn and the agent's, hollow for the client's. */
export function TurnDot({ owner, size = 7 }: { owner: TurnOwner; size?: 6 | 7 }) {
  const hollow = owner === "client" || owner === "late";
  const ink = owner === "you" ? "var(--co-accent-ink)" : owner === "late" ? "var(--co-alert-ink)" : owner === "none" ? "var(--co-faint)" : "var(--co-muted)";
  return (
    <span
      aria-hidden="true"
      className="shrink-0 rounded-full"
      style={{ width: size, height: size, ...(hollow ? { boxShadow: `inset 0 0 0 1.5px ${ink}` } : { backgroundColor: ink }) }}
    />
  );
}

/** The rail shows five dates at a time (Figma 08 "Calendar / Date rail"); more scroll sideways. */
const VISIBLE_DATES = 5;
/** A date never goes narrower than this; a narrow rail shows fewer than five. */
const DATE_MIN_WIDTH = 150;
/** A date's side padding (16 each side). */
const DATE_PADDING = 32;
/** About one character of the 13px Manrope title. */
const TITLE_CHAR_PX = 7.2;

/** The title's first whole words, so the rail never cuts mid-word (Figma: "Tracing the Bedouin roots of…"). */
function firstWords(title: string, max: number): string {
  const clean = title.trim();
  if (clean.length <= max) return clean;
  let out = "";
  for (const word of clean.split(/\s+/)) {
    const next = out ? `${out} ${word}` : word;
    if (next.length > max) break;
    out = next;
  }
  return `${(out || clean.slice(0, max)).replace(/[,.:;!?-]+$/, "")}…`;
}

function formatShort(slot: CalendarPlanSlot): string {
  return `${MEDIA_TYPE_LABEL[slot.mediaType]} ${displayAspectRatio(slot)}`;
}

/**
 * The Date Rail (DESIGN.md): a 96px planning index across the top of the post
 * page. The month block, then the posts' dates with their day, format, title,
 * whose turn it is and the stage track. Five dates fill the rail; a longer
 * month scrolls sideways a whole date at a time, the open one kept in view.
 * The open date sits on the raised surface with a 3px accent bar on its top
 * edge only.
 */
export default function DateRail({
  monthLabel,
  countLine,
  slots,
  selectedId,
  states,
  onSelect,
  onBack,
  audience = "team",
}: {
  /** The client's side hears its own stage words. */
  audience?: "team" | "client";
  monthLabel: string;
  countLine: string;
  slots: CalendarPlanSlot[];
  selectedId: string;
  states: Map<string, PostState>;
  onSelect: (slotId: string) => void;
  onBack: () => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedIndex = Math.max(0, slots.findIndex((slot) => slot.id === selectedId));
  // Roving focus: arrows move it, Enter (or a click) opens the date.
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const activeIndex = focusIndex ?? selectedIndex;
  const scrolls = slots.length > VISIBLE_DATES;
  // The list's width, for how many characters of a title fit one date.
  const [listWidth, setListWidth] = useState(0);
  const dateWidth = listWidth > 0 ? Math.max(DATE_MIN_WIDTH, listWidth / Math.min(VISIBLE_DATES, Math.max(1, slots.length))) : DATE_MIN_WIDTH;
  const titleChars = Math.max(8, Math.floor((dateWidth - DATE_PADDING) / TITLE_CHAR_PX));

  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => setListWidth(Math.floor(entries[0]?.contentRect.width ?? 0)));
    observer.observe(list);
    return () => observer.disconnect();
  }, []);

  // The open date stays in view: centred when the page opens, then moved in
  // from the nearest edge only when it is out of view (a click on a visible
  // date never slides the rail under the pointer).
  const placed = useRef(false);
  useEffect(() => {
    const list = listRef.current;
    const node = list?.querySelector<HTMLElement>(`[data-slot-id="${selectedId}"]`);
    if (!list || !node) return;
    const smooth = placed.current && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // The list is the dates' offset parent, so this is the date's place on the strip.
    const start = node.offsetLeft;
    let left: number | null = null;
    if (!placed.current) left = start - (list.clientWidth - node.offsetWidth) / 2;
    else if (start < list.scrollLeft) left = start;
    else if (start + node.offsetWidth > list.scrollLeft + list.clientWidth) left = start + node.offsetWidth - list.clientWidth;
    placed.current = true;
    if (left !== null) list.scrollTo({ left: Math.max(0, left), behavior: smooth ? "smooth" : "auto" });
  }, [selectedId, slots.length]);

  const moveFocus = (index: number) => {
    const next = Math.max(0, Math.min(slots.length - 1, index));
    setFocusIndex(next);
    listRef.current?.querySelector<HTMLButtonElement>(`[data-date-index="${next}"]`)?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus(index + 1);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      moveFocus(0);
    } else if (event.key === "End") {
      event.preventDefault();
      moveFocus(slots.length - 1);
    }
  };

  return (
    // Calendar / Date rail (library, 13 Oct): an island, radius 28, the chrome frost the
    // nav uses, no hairline at its foot, stuck at the top while the post scrolls.
    // 108 tall: date, title, turn and track fit without
    // the flex column shrinking a row.
    <nav aria-label="Posts this month" className="co-post-rail-island flex h-[108px] w-full overflow-hidden rounded-[28px]">
      <div className="flex w-[176px] shrink-0 flex-col gap-[5px] border-r border-co-line p-4">
        <button
          type="button"
          onClick={onBack}
          aria-label={`Back to ${monthLabel}`}
          className="inline-flex min-w-0 items-center gap-1 self-start text-[14px] font-semibold leading-[19px] text-co-text transition-colors hover:text-co-accent-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
        >
          <ChevronLeft className="size-4 shrink-0" strokeWidth={2} aria-hidden="true" />
          <span className="truncate">{monthLabel}</span>
        </button>
        <p className="truncate text-[12px] leading-4 text-co-muted">{countLine}</p>
      </div>

      <div
        ref={listRef}
        role="listbox"
        aria-label="Choose a post"
        aria-orientation="horizontal"
        className={cn(
          "custom-scrollbar relative flex min-w-0 flex-1 overflow-x-auto overflow-y-hidden",
          // Whole dates only: a scroll comes to rest on a date's edge.
          scrolls && "snap-x snap-mandatory",
        )}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusIndex(null);
        }}
      >
        {slots.map((slot, index) => {
          const open = slot.id === selectedId;
          const state = states.get(slot.id);
          const date = new Date(slot.scheduledAt);
          const weekday = date.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
          return (
            <button
              key={slot.id}
              type="button"
              role="option"
              aria-selected={open}
              data-slot-id={slot.id}
              data-date-index={index}
              title={slot.title}
              tabIndex={index === activeIndex ? 0 : -1}
              onClick={() => onSelect(slot.id)}
              onFocus={() => setFocusIndex(index)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cn(
                "@container relative flex h-full min-w-[150px] flex-col gap-[5px] px-4 pb-2.5 pt-[13px] text-left transition-colors duration-[var(--duration-fast)] last:border-r-0",
                // Up to five share the rail; past five each takes a fifth and the rest scroll.
                scrolls ? "shrink-0 grow-0 basis-1/5 snap-start" : "flex-1 basis-0",
                "border-r border-co-line focus-visible:z-[1] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent",
                open ? "co-post-rail-open" : "hover:bg-co-hover",
              )}
            >
              {open ? <span aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px] bg-co-accent" /> : null}
              <span className="flex min-w-0 shrink-0 items-end gap-[7px]">
                <span
                  className={cn(
                    "font-section text-[24px] font-bold leading-none tabular-nums",
                    open ? "text-co-accent" : "text-co-text",
                  )}
                >
                  {date.getUTCDate()}
                </span>
                <span className="text-[12px] leading-4 text-co-muted">{weekday}</span>
                <span className="ml-auto hidden truncate text-[11.5px] leading-4 text-co-muted @[170px]:inline">
                  {formatShort(slot)}
                </span>
              </span>
              <span
                className={cn(
                  "h-[17px] shrink-0 truncate text-[13px] leading-[17px]",
                  open ? "font-semibold text-co-text" : "font-medium text-co-text2",
                )}
              >
                {slot.title.trim().length > titleChars ? (
                  <>
                    <span aria-hidden="true">{firstWords(slot.title, titleChars)}</span>
                    <span className="sr-only">{slot.title}</span>
                  </>
                ) : (
                  slot.title
                )}
              </span>
              {state ? (
                <span className="flex min-w-0 shrink-0 items-center gap-1.5 text-[12px] leading-4" style={{ color: turnInk(state.turn.owner, state.turn.alert) }}>
                  <TurnDot size={6} owner={state.turn.alert ? "late" : state.turn.owner} />
                  <span className="truncate">{state.turn.label}</span>
                </span>
              ) : null}
              <span className="mt-auto shrink-0">
                {state ? <StageTrack size="rail" audience={audience} stageIndex={state.stageIndex} stops={state.stops} /> : null}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
